import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import { apiUrl } from "../components/LoginSignup";

const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/agreement-template.docx`;
const FALLBACK_TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/SHIV SHAKTI COTTON MILLS  (1).docx`;

let templateCache = null;

const formatBookingDate = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "N/A";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatAmount = (amount) =>
  Number(amount || 0).toLocaleString("en-IN");

const formatCommission = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const text = String(value).trim();
  return text.includes("%") ? text : `${text}%`;
};

const formatServices = (services) => {
  if (Array.isArray(services) && services.length > 0) {
    return services.filter(Boolean).join(", ");
  }
  if (services && typeof services === "string") {
    return services.trim() || "N/A";
  }
  return "N/A";
};

export const getAgreementData = (booking) => {
  const received =
    (Number(booking.term_1) || 0) +
    (Number(booking.term_2) || 0) +
    (Number(booking.term_3) || 0);

  return {
    DATE: formatBookingDate(booking.date),
    COMPANY_NAME: (booking.company_name || "N/A").toUpperCase(),
    ADDRESS: booking.address || booking.state || "N/A",
    SERVICES: formatServices(booking.services),
    TOTAL_PAYMENT: `Rs ${formatAmount(booking.total_amount)}/-including GST `,
    RECEIVED_PAYMENT: formatAmount(received),
    COMMISSION: formatCommission(booking.after_disbursement),
  };
};

const getFileName = (booking, extension) => {
  const company = (booking.company_name || "agreement")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "_");
  return `${company || "agreement"}_agreement.${extension}`;
};

const loadTemplate = async () => {
  if (templateCache) return templateCache;

  let response = await fetch(TEMPLATE_URL);
  if (!response.ok) {
    response = await fetch(FALLBACK_TEMPLATE_URL);
  }
  if (!response.ok) {
    throw new Error(
      "Agreement template not found. Please ensure agreement-template.docx exists in the public folder."
    );
  }

  templateCache = await response.arrayBuffer();
  return templateCache;
};

const generateFilledDocx = async (booking) => {
  const templateBuffer = await loadTemplate();
  const zip = new PizZip(templateBuffer);
  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
  });

  doc.setData(getAgreementData(booking));
  doc.render();

  return doc.getZip().generate({
    type: "arraybuffer",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
};

export const downloadAgreementDocx = async (booking) => {
  const buffer = await generateFilledDocx(booking);
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  saveAs(blob, getFileName(booking, "docx"));
};

export const downloadAgreementPdf = async (booking) => {
  const userSession = JSON.parse(localStorage.getItem("userSession") || "{}");

  if (!userSession?.token) {
    throw new Error("Please log in again to download the agreement.");
  }

  const response = await fetch(`${apiUrl}/booking/agreement/pdf`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      authorization: userSession.token,
    },
    body: JSON.stringify(booking),
  });

  const contentType = response.headers.get("content-type") || "";

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || "Failed to generate agreement PDF. Please try again."
    );
  }

  if (!contentType.includes("application/pdf")) {
    throw new Error("Server did not return a valid PDF file.");
  }

  const arrayBuffer = await response.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);

  const isPdf =
    bytes.length > 500 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46;

  if (!isPdf) {
    throw new Error(
      "Downloaded file is corrupted or not a PDF. Please restart the backend and try again."
    );
  }

  const blob = new Blob([bytes], { type: "application/pdf" });
  saveAs(blob, getFileName(booking, "pdf"));
};

export const downloadAgreement = async (booking, format = "pdf") => {
  if (format === "docx") {
    await downloadAgreementDocx(booking);
    return;
  }
  await downloadAgreementPdf(booking);
};
